import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Home, Package, CheckCircle, Clock, TrendingUp } from 'lucide-react';
import { toast } from 'react-toastify';
import { CardStats, ConfirmModal } from '@components/ui';
import MaterialPageLayout from '../../components/MaterialPageLayout';
import MaterialDetailModal from '../../components/MaterialDetailModal';
import { ProgressIndicator, formatCurrency } from '../../components/StockIndicator';
import CatiTable from './CatiTable';
import CatiModal from './CatiModal';
import ImalatModal from './ImalatModal';
import { catiKategorileri, getCatiByInsaat } from '@mocks/materials/cati';
import OdemePlanMiniModal from '@components/shared/OdemePlanMiniModal';
import { addMockCariHareket } from '@mocks/cariHesaplar';

export default function Cati() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const [filterKategori, setFilterKategori] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isImalatOpen, setIsImalatOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isOdemePlanOpen, setIsOdemePlanOpen] = useState(false);
    const [odemePlanData, setOdemePlanData] = useState(null);

    const insaatId = showAllConstructions ? null : selectedConstruction?.id;

    const filteredData = useMemo(() => {
        let data = getCatiByInsaat(insaatId);
        if (filterKategori) data = data.filter(i => i.kategori === filterKategori);
        return data;
    }, [insaatId, filterKategori]);

    const stats = useMemo(() => {
        const toplamKalem = filteredData.length;
        const tamamlanan = filteredData.filter(s => s.imalatDurumu === 'tamamlandi').length;
        const devamEden = filteredData.filter(s => s.imalatDurumu === 'devam').length;
        const toplamMaliyet = filteredData.reduce((acc, s) => acc + s.toplamFiyat, 0);
        const toplamMetrekare = filteredData
            .filter(s => s.metrekareKarsiligi)
            .reduce((acc, s) => acc + s.metrekareKarsiligi, 0);
        return { toplamKalem, tamamlanan, devamEden, toplamMaliyet, toplamMetrekare };
    }, [filteredData]);

    const handleAdd = () => { setSelectedItem(null); setIsModalOpen(true); };
    const handleView = (item) => { setSelectedItem(item); setIsDetailOpen(true); };
    const handleEdit = (item) => { setSelectedItem(item); setIsModalOpen(true); };
    const handleDelete = (item) => { setSelectedItem(item); setIsDeleteOpen(true); };
    const handleUpdateImalat = (item) => { setSelectedItem(item); setIsImalatOpen(true); };

    const handleSubmit = async (fd, isEdit) => {
        setIsLoading(true);
        await new Promise(r => setTimeout(r, 800));

        if (!isEdit && fd.supplierId && fd.kdvDahilToplam > 0) {
            addMockCariHareket(fd.supplierId, insaatId || 'c1', {
                type: 'purchase',
                subtype: 'fatura',
                amount: fd.kdvDahilToplam,
                date: fd.siparisTarihi || new Date().toISOString().split('T')[0],
                description: `Çatı Malzemesi — ${fd.miktar} ${fd.birim} ${fd.kategori}`,
            });
        }

        toast.success(isEdit ? 'Güncellendi' : 'Yeni kayıt eklendi');
        setIsLoading(false);
        setIsModalOpen(false);

        if (!isEdit && fd.supplierId && fd.kdvDahilToplam > 0) {
            setOdemePlanData({ amount: fd.kdvDahilToplam, companyName: fd.tedarikci });
            setIsOdemePlanOpen(true);
        }
    };

    const handleOdemePlanConfirm = ({ nextDueDate }) => {
        toast.success(`Ödeme planı oluşturuldu — Vade: ${nextDueDate}`);
        setIsOdemePlanOpen(false);
        setOdemePlanData(null);
    };
    const handleConfirmDelete = async () => { setIsLoading(true); await new Promise(r => setTimeout(r, 500)); toast.success('Silindi'); setIsLoading(false); setIsDeleteOpen(false); };
    const handleImalatUpdate = async (id) => { setIsLoading(true); await new Promise(r => setTimeout(r, 500)); toast.success(`İmalat güncellendi: %${id.imalatYuzde}`); setIsLoading(false); setIsImalatOpen(false); };

    const statsCards = [
        { title: 'Toplam Sipariş', value: `${stats.toplamMetrekare} m²`, icon: Package, color: 'primary' },
        { title: 'Tamamlanan', value: `${stats.tamamlanan}`, icon: CheckCircle, color: 'success' },
        { title: 'Devam Eden', value: `${stats.devamEden}`, icon: Clock, color: 'warning' },
        { title: 'Toplam Maliyet', value: formatCurrency(stats.toplamMaliyet), icon: TrendingUp, color: 'info' },
    ];

    const detailFields = [{ key: 'siparisTarihi', label: 'Sipariş Tarihi', type: 'date' }, { key: 'kategori', label: 'Kategori' }, { key: 'urunAdi', label: 'Ürün Adı' }, { key: 'aciklama', label: 'Açıklama', fullWidth: true }, { key: 'miktar', label: 'Miktar', type: 'number' }, { key: 'imalatYuzde', label: 'İmalat %' }, { key: 'toplamFiyat', label: 'Tutar', type: 'currency' }, { key: 'imalatDurumu', label: 'Durum', type: 'status' }];

    const headerFilter = (
        <select
            value={filterKategori}
            onChange={(e) => setFilterKategori(e.target.value)}
            className="px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50 min-w-[160px]"
        >
            <option value="">Tüm Kategoriler</option>
            {catiKategorileri.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
    );

    return (
        <>
            <MaterialPageLayout title="Çatı" subtitle="İnce İşler" icon={Home} iconBgColor="bg-error" backLink="/insaat/ince" stats={statsCards} onAddClick={handleAdd} headerFilter={headerFilter}>
                <div className="p-4 border-b border-border-light bg-bg-tertiary/50"><ProgressIndicator completed={stats.tamamlanan} total={stats.toplamKalem} label="İmalat İlerlemesi" /></div>
                <CatiTable data={filteredData} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} onUpdateImalat={handleUpdateImalat} />
            </MaterialPageLayout>
            <CatiModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleSubmit} initialData={selectedItem} isLoading={isLoading} />
            <MaterialDetailModal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} data={selectedItem} fields={detailFields} title="Çatı Malzemesi Detayı" />
            <ImalatModal isOpen={isImalatOpen} onClose={() => setIsImalatOpen(false)} onSubmit={handleImalatUpdate} data={selectedItem} isLoading={isLoading} />
            <ConfirmModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={handleConfirmDelete} title="Kaydı Sil" message={`"${selectedItem?.urunAdi}" kaydını silmek istediğinize emin misiniz?`} isLoading={isLoading} />
            <OdemePlanMiniModal
                isOpen={isOdemePlanOpen}
                onClose={() => { setIsOdemePlanOpen(false); setOdemePlanData(null); }}
                onConfirm={handleOdemePlanConfirm}
                amount={odemePlanData?.amount}
                companyName={odemePlanData?.companyName}
            />
        </>
    );
}
